/**
 * Mobile Feature Configuration
 * Defines which features are enabled/disabled on mobile devices
 */

import type { TabValue } from '../components/AppShell';
import type { WidgetKey } from '../types/widgets';

export interface MobileFeatureConfig {
  // Main tabs visibility
  tabs: {
    calculator: boolean;
    profile: boolean;
    dasha: boolean;
    feeds: boolean;
    mychart: boolean;
    chat: boolean;
    family: boolean;
    admin: boolean;
  };
  
  // Calculator tab features
  calculator: {
    userForm: boolean;
    lifeAreasDashboard: boolean;
    basicDetails: boolean;
    d1Chart: boolean;
    d1ChartSimplified: boolean; // Show simplified version without aspects
    dashaTimeline: boolean;
    dashaTimelineVertical: boolean; // Use vertical list instead of horizontal timeline
    planetDetails: boolean;
    yogasWidget: boolean;
    fiveFoldFriends: boolean;
    divisionalChartsTable: boolean;
    shadbalaTable: boolean;
    ishtaKashtaTable: boolean;
    
    // Sub-tabs
    divisionalTab: boolean; // D9/D10/etc
    transitTab: boolean;
    vastuTab: boolean;
    chalitTab: boolean;
  };
  
  // Current Period (Dasha) tab
  dasha: {
    timeline: boolean;
    feedsList: boolean;
    futureAlerts: boolean;
  };
  
  // Yogas & Predictions (Feeds) tab
  feeds: {
    permanentYogas: boolean;
    activeYogas: boolean;
    predictions: boolean;
    exportPdf: boolean;
  };
  
  // My Chart tab (Advanced features)
  mychart: {
    advancedChart: boolean;
    housePredictions: boolean;
    interpretations: boolean;
  };
  
  // Chat tab
  chat: {
    enabled: boolean;
    lifeAreaButtons: boolean;
    contextPanel: boolean;
  };
  
  // Notifications
  notifications: {
    enabled: boolean;
  };
}

/**
 * Mobile configuration - limited features for smaller screens
 */
export const MOBILE_FEATURES: MobileFeatureConfig = {
  tabs: {
    calculator: true,    // Home tab - essential
    profile: false,      // Hide advanced chart
    dasha: true,         // Current period - important
    feeds: true,         // Predictions - important
    mychart: true,       // Detailed synopsis - now available on mobile via hamburger menu
    chat: true,          // Chat works great on mobile
    family: false,       // Family manager - table heavy
    admin: false,        // Admin - desktop only
  },
  
  calculator: {
    userForm: true,
    lifeAreasDashboard: true,
    basicDetails: true,
    d1Chart: true,
    d1ChartSimplified: true,
    dashaTimeline: true,
    dashaTimelineVertical: true,
    planetDetails: false,     // Too dense
    yogasWidget: true,
    fiveFoldFriends: false,   // Table format
    divisionalChartsTable: false, // Wide table
    shadbalaTable: false,     // Complex table
    ishtaKashtaTable: false,  // Complex table
    
    divisionalTab: false,     // Multiple charts
    transitTab: false,        // Complex dual chart
    vastuTab: true,           // Enabled on mobile (RoomPlanner hides full matrix)
    chalitTab: false,         // Advanced feature
  },
  
  dasha: {
    timeline: true,
    feedsList: true,
    futureAlerts: true,
  },
  
  feeds: {
    permanentYogas: true,
    activeYogas: true,
    predictions: true,
    exportPdf: false,  // PDF not mobile-friendly
  },
  
  mychart: {
    advancedChart: false,
    housePredictions: false,
    interpretations: false,
  },
  
  chat: {
    enabled: true,
    lifeAreaButtons: true,
    contextPanel: true,
  },
  
  notifications: {
    enabled: true,
  },
};

/**
 * Desktop configuration - all features enabled
 */
export const DESKTOP_FEATURES: MobileFeatureConfig = {
  tabs: {
    calculator: true,
    profile: true,
    dasha: true,
    feeds: true,
    mychart: true,
    chat: true,
    family: true,
    admin: true,  // Controlled by role separately
  },
  
  calculator: {
    userForm: true,
    lifeAreasDashboard: true,
    basicDetails: true,
    d1Chart: true,
    d1ChartSimplified: false, // Use full chart
    dashaTimeline: true,
    dashaTimelineVertical: false, // Use horizontal timeline
    planetDetails: true,
    yogasWidget: true,
    fiveFoldFriends: true,
    divisionalChartsTable: true,
    shadbalaTable: true,
    ishtaKashtaTable: true,
    
    divisionalTab: true,
    transitTab: true,
    vastuTab: true,
    chalitTab: true,
  },
  
  dasha: {
    timeline: true,
    feedsList: true,
    futureAlerts: true,
  },
  
  feeds: {
    permanentYogas: true,
    activeYogas: true,
    predictions: true,
    exportPdf: true,
  },
  
  mychart: {
    advancedChart: true,
    housePredictions: true,
    interpretations: true,
  },
  
  chat: {
    enabled: true,
    lifeAreaButtons: true,
    contextPanel: true,
  },
  
  notifications: {
    enabled: true,
  },
};

/**
 * Get feature configuration based on device type
 */
export function getFeatureConfig(isMobile: boolean): MobileFeatureConfig {
  return isMobile ? MOBILE_FEATURES : DESKTOP_FEATURES;
}

/**
 * Get enabled tabs for navigation
 */
export function getEnabledTabs(isMobile: boolean, isAdmin: boolean): TabValue[] {
  const config = getFeatureConfig(isMobile);
  const tabs: TabValue[] = [];
  
  if (config.tabs.calculator) tabs.push('calculator');
  if (config.tabs.dasha) tabs.push('dasha');
  if (config.tabs.feeds) tabs.push('feeds');
  if (config.tabs.mychart) tabs.push('mychart');
  if (config.tabs.chat) tabs.push('chat');
  if (config.tabs.family) tabs.push('family');
  if (config.tabs.admin && isAdmin) tabs.push('admin');
  
  return tabs;
}

/**
 * Get enabled widgets for calculator tab
 */
export function getEnabledWidgets(isMobile: boolean): WidgetKey[] {
  const config = getFeatureConfig(isMobile);
  const widgets: WidgetKey[] = [];
  
  if (config.calculator.d1Chart) widgets.push('north_indian_chart_d1');
  if (config.calculator.lifeAreasDashboard) widgets.push('life_areas_dashboard');
  if (config.calculator.dashaTimeline) widgets.push('vimshottari_timeline');
  if (config.calculator.basicDetails) widgets.push('basic_details');
  if (config.calculator.planetDetails) widgets.push('planet_details');
  if (config.calculator.yogasWidget) widgets.push('yogas_analysis');
  if (config.calculator.fiveFoldFriends) widgets.push('five_fold_friends');
  if (config.calculator.divisionalChartsTable) widgets.push('shodashavarga_table');
  if (config.calculator.shadbalaTable) widgets.push('shadbala_table');
  if (config.calculator.ishtaKashtaTable) widgets.push('ishta_kashta_table');
  
  return widgets;
}
